import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SerifGlowInView } from "./SerifGlow";

const ASSET = "https://qclay.design/lovable/bags";
const TEXT_COLOR = "#545454";

// Handmade crochet knitted plush toys — real transparent-background PNGs,
// linked to Knitted Collection products in the Shop database.
const BAGS = [
  {
    img: "https://public.readdy.ai/ai/img_res/8daddeb1-db37-4d8d-ab84-337805934afe.png",
    baseAngle: 270,
    label: "(01)",
    name: "Bunny",
    productId: 100006,
    bobDelay: 0,
  },
  {
    img: "https://public.readdy.ai/ai/img_res/ed9ea97f-8fcd-4393-ac32-8523dcabac2b.png",
    baseAngle: 330,
    label: "(02)",
    name: "Bear",
    productId: 100007,
    bobDelay: 0.5,
  },
  {
    img: "https://public.readdy.ai/ai/img_res/8cd0bb0d-b54c-4ce5-8bb0-e38afa59c84d.png",
    baseAngle: 30,
    label: "(03)",
    name: "Ellie",
    productId: 100008,
    bobDelay: 1.0,
  },
  {
    img: "https://public.readdy.ai/ai/img_res/0ce5c2d6-4ea1-4907-9232-fa484910744e.png",
    baseAngle: 150,
    label: "(04)",
    name: "Miu",
    productId: 100009,
    bobDelay: 1.5,
  },
  {
    img: "https://public.readdy.ai/ai/img_res/6d2a0798-4cf2-49bb-be12-79253ef1773a.png",
    baseAngle: 210,
    label: "(05)",
    name: "Foxie",
    productId: 100010,
    bobDelay: 2.0,
  },
  {
    img: "https://public.readdy.ai/ai/img_res/e28742b8-5a01-43ba-adee-2ae8d9846b99.png",
    baseAngle: 90,
    label: "(06)",
    name: "Bloop",
    productId: 100011,
    bobDelay: 2.5,
  },
];

const ORBIT_RADIUS = 280;
const BAG_HALF = 120;
const LABEL_GAP = 14;
const LABEL_DIST = BAG_HALF + LABEL_GAP;

export default function PerfectMatch() {
  const [angle, setAngle] = useState(0);
  const [paused, setPaused] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const tick = () => {
      setAngle((prev) => prev + 0.12);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused]);

  return (
    <section
      style={{
        background: "#f7f7f7",
        minHeight: "100vh",
        paddingBottom: 80,
        fontFamily: "'Inter Tight', sans-serif",
        position: "relative",
        overflow: "visible",
      }}
    >
      {/* Torn paper top edge */}
      <img
        src={`${ASSET}/paper.png`}
        alt=""
        style={{
          position: "absolute",
          top: -188,
          left: 0,
          right: 0,
          width: "100%",
          height: "auto",
          objectFit: "cover",
          objectPosition: "top center",
          zIndex: 50,
          pointerEvents: "none",
        }}
      />

      {/* Top badge text */}
      <motion.div
        initial={{ opacity: 0, filter: "blur(8px)", y: 12 }}
        whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          paddingTop: 120,
          zIndex: 60,
          position: "relative",
          textAlign: "center",
        }}
      >
        {["HAND-KNITTED WITH LOVE.", "STITCH BY STITCH, JUST FOR THEM."].map((line) => (
          <div
            key={line}
            style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "2.5px",
              lineHeight: 1.8,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            {line}
          </div>
        ))}
      </motion.div>

      {/* Orbit stage */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 780,
          zIndex: 60,
          marginTop: 20,
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Center title */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
          whileInView={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
                fontSize: 68,
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: "-1.5px",
                color: TEXT_COLOR,
              }}
            >
              Find your
            </div>
            <div
              className="flex items-baseline justify-center"
              style={{ gap: 0 }}
            >
              <span
                style={{
                  fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
                  fontSize: 68,
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: "-1.5px",
                  color: TEXT_COLOR,
                }}
              >
                little&nbsp;
              </span>
              <SerifGlowInView
                word="friend"
                fontSize={64}
                lineHeight={64}
                letterSpacing={-2}
                strokeWidth={14}
                delay={0.5}
              />
            </div>
          </div>
        </motion.div>

        {/* Orbit center */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 0,
            height: 0,
          }}
        >
          {BAGS.map((bag) => {
            const rad = ((angle + bag.baseAngle) * Math.PI) / 180;
            const x = Math.cos(rad) * ORBIT_RADIUS;
            const y = Math.sin(rad) * ORBIT_RADIUS;
            const labelX = Math.cos(rad) * LABEL_DIST;
            const labelY = Math.sin(rad) * LABEL_DIST;

            return (
              <div key={bag.img}>
                {/* Plush toy image with gentle bob float, clickable → product detail */}
                <motion.div
                  style={{
                    position: "absolute",
                    left: x - BAG_HALF,
                    top: y - BAG_HALF,
                    width: BAG_HALF * 2,
                    height: BAG_HALF * 2,
                    cursor: "pointer",
                    zIndex: 5,
                  }}
                  whileHover={{ scale: 1.12, rotate: [0, -6, 6, 0], transition: { duration: 0.5 } }}
                >
                  <Link
                    to={`/product/${bag.productId}`}
                    aria-label={`Shop ${bag.name}`}
                    style={{ display: "block", width: "100%", height: "100%" }}
                  >
                    <motion.img
                      src={bag.img}
                      alt={bag.name}
                      animate={{ y: [0, -14, 0] }}
                      transition={{
                        duration: 3.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: bag.bobDelay,
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        pointerEvents: "none",
                        filter: "drop-shadow(0 12px 18px rgba(84,84,84,0.10))",
                      }}
                    />
                  </Link>
                </motion.div>

                {/* Orbiting label just outside the plush */}
                <div
                  style={{
                    position: "absolute",
                    left: labelX,
                    top: labelY,
                    transform: "translate(-50%, -50%)",
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontSize: 18,
                    fontWeight: 400,
                    color: "rgba(84,84,84,0.6)",
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {bag.name} {bag.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer strip */}
      <div
        className="flex items-center justify-between"
        style={{
          maxWidth: 1200,
          margin: "40px auto 0",
          padding: "0 32px",
          color: TEXT_COLOR,
          fontFamily: "'Inter Tight', sans-serif",
          fontSize: 12,
          fontWeight: 400,
          letterSpacing: "1.5px",
        }}
      >
        <span>© 2026 MAISON DES JOUETS</span>
        <span style={{ opacity: 0.55 }}>KNITTED WITH CARE — MILANO / PARIS</span>
        <div className="flex" style={{ gap: 20 }}>
          <a href="#" className="cursor-pointer hover:opacity-60 transition-opacity">Instagram</a>
          <a href="#" className="cursor-pointer hover:opacity-60 transition-opacity">Pinterest</a>
          <a href="#" className="cursor-pointer hover:opacity-60 transition-opacity">TikTok</a>
        </div>
      </div>
    </section>
  );
}